// Compact, human-readable one-liners of the owner-insight cards, fed to the
// AI Battery Coach so its narrative reflects the same findings the owner
// sees on the Care/Predict cards. Mirrors the card formulas.
import { avgDailyMiles } from "./batteryAnalytics";

export function warrantyForecastSummary(vehicle) {
  const soh = vehicle?.sohCurrent;
  if (soh == null) return null;
  const floor = vehicle?.warranty?.threshold ?? 70;
  const years = vehicle?.warranty?.years ?? 8;
  const milesCap = vehicle?.warranty?.miles ?? 120000;
  const velocity = Math.max(0.05, Math.abs(vehicle?.delta30d ?? 0.2));
  const start = new Date(vehicle?.year || new Date().getFullYear() - 3, 5, 1);
  const end = new Date(start); end.setFullYear(start.getFullYear() + years);
  const monthsLeftTime = Math.max(0, (end.getTime() - Date.now()) / (30.44 * 86400e3));
  const dailyMi = avgDailyMiles(vehicle);
  const milesLeft = Math.max(0, milesCap - (vehicle?.odometer ?? 0));
  const monthsLeftMiles = dailyMi > 0 ? milesLeft / (dailyMi * 30.44) : monthsLeftTime;
  const monthsLeft = Math.min(monthsLeftTime, monthsLeftMiles);
  // Sub-linear Li-ion fade (same model as forecastDegradation /
  // futureHealthForecast): loss(m) = vel·12·(m/12)^0.75. A straight
  // vel×months line overstates multi-year loss by ~40%.
  const projected = +(soh - velocity * 12 * Math.pow(monthsLeft / 12, 0.75)).toFixed(1);
  const margin = +(projected - floor).toFixed(1);
  const verdict = margin >= 3 ? "unlikely to qualify for a warranty claim (pack outpacing the floor)"
    : margin >= -1 ? "borderline — projection lands within a point of the floor, monitor monthly"
    : "trending toward warranty-claim eligibility before coverage ends";
  return `Projected ${projected}% SoH when coverage ends (~${Math.round(monthsLeft)} mo) vs ${floor}% floor → ${verdict}`;
}

export function winterTaxSummary(vehicle, climate) {
  const epa = vehicle?.epaRange || 300;
  const winterLow = climate?.winter_low_f ?? 26;
  const anchors = [[50, 5], [40, 10], [32, 15], [20, 23], [8, 30], [0, 34]];
  let raw = anchors[anchors.length - 1][1];
  if (winterLow >= anchors[0][0]) raw = anchors[0][1];
  else for (let i = 0; i < anchors.length - 1; i++) {
    const [t1, l1] = anchors[i], [t2, l2] = anchors[i + 1];
    if (winterLow <= t1 && winterLow >= t2) { raw = l1 + ((t1 - winterLow) / (t1 - t2)) * (l2 - l1); break; }
  }
  const pump = vehicle?.model === "Model Y" || vehicle?.model === "Cybertruck" || (vehicle?.year || 2021) >= 2021;
  const lossPct = Math.round(raw * (pump ? 0.72 : 1));
  const lossMi = Math.round((epa * lossPct) / 100);
  return `Winter range tax in ${climate?.label || "their climate"}: −${lossPct}% (−${lossMi} mi) on cold days; preconditioning while plugged in recovers ~${Math.round(lossMi * 0.4)} mi; ${pump ? "heat pump" : "resistive heating"}`;
}

export function liveTempSummary(vehicle, floor, soh, vel) {
  const liveC = typeof vehicle?.outsideTempC === "number" ? vehicle.outsideTempC : null;
  if (liveC == null) return null;
  const tempF = Math.round((liveC * 9) / 5 + 32);
  const mult = Math.min(2.2, Math.max(0.55, Math.pow(2, (liveC - 21) / 12)));
  const mtf = vel > 0 ? (soh - floor) / vel : 999;
  const deltaMo = Math.round(Math.min(240, mtf) - Math.min(240, mtf / mult));
  const locked = tempF < 50 ? Math.min(15, Math.round((50 - tempF) * 0.4)) : 0;
  return `Live outside temp today ${tempF}°F — if sustained year-round the pack reaches the ${floor}% floor ~${Math.abs(deltaMo)} mo ${deltaMo > 0 ? "sooner" : "later"}; ${locked > 0 ? `~${locked}% SoC temporarily cold-locked` : "no SoC cold-lock"}`;
}

export function chargeDisciplineSummary(vehicle) {
  const limit = typeof vehicle?.chargeLimit === "number" ? vehicle.chargeLimit : null;
  if (limit == null) return null;
  const lfp = /rwd|standard/i.test(vehicle?.trim || "") &&
    (vehicle?.model === "Model 3" || vehicle?.model === "Model Y");
  if (lfp) return `LFP pack, daily charge limit ${limit}% (Tesla recommends 100% weekly for LFP)`;
  const grade = limit <= 80 ? "excellent" : limit <= 90 ? "good" : "aggressive (accelerates calendar aging)";
  return `Daily charge limit set to ${limit}% — ${grade}`;
}

export function coachInsights(vehicle, climate, vel) {
  const soh = vehicle?.sohCurrent ?? 92;
  const floor = vehicle?.warranty?.threshold ?? 70;
  const out = {};
  const w = warrantyForecastSummary(vehicle);
  const winter = winterTaxSummary(vehicle, climate);
  const temp = liveTempSummary(vehicle, floor, soh, vel);
  const disc = chargeDisciplineSummary(vehicle);
  if (w) out.warranty_forecast = w;
  if (winter) out.winter_range_tax = winter;
  if (temp) out.live_temperature_today = temp;
  if (disc) out.charge_limit_discipline = disc;
  return out;
}
